/**
 * Chamadas HTTP das pessoas da família.
 *
 * Papel: aqui ficam as quatro chamadas de `/api/pessoas-familia` que a tela usa
 * — listar, criar, alterar e excluir uma pessoa.
 *
 * Como usar: a listagem devolve o array inteiro, e não uma página. Nenhuma
 * transformação acontece aqui — o que sai é o que o schema validou.
 */
import {
  type NovaPessoaFamilia,
  type PessoaFamilia,
  pessoaFamiliaSchema,
} from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** A listagem inteira, sem envelope de paginação. */
const listaDePessoasSchema = z.array(pessoaFamiliaSchema);

/**
 * Lê todas as pessoas da família, em ordem alfabética de nome completo.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As pessoas cadastradas, possivelmente nenhuma.
 */
export function buscarPessoasFamilia(sinal?: AbortSignal): Promise<PessoaFamilia[]> {
  return clienteApi.get('/pessoas-familia', { schema: listaDePessoasSchema, sinal });
}

/**
 * Cria uma pessoa da família.
 *
 * A API responde 409 quando já existe pessoa com o mesmo `nomeCompleto`.
 *
 * @param dados - A pessoa preenchida no formulário, já validada por
 *   `novaPessoaFamiliaSchema`.
 * @returns A pessoa como ficou gravada, com identificador e carimbos.
 */
export function criarPessoaFamilia(dados: NovaPessoaFamilia): Promise<PessoaFamilia> {
  return clienteApi.post('/pessoas-familia', dados, { schema: pessoaFamiliaSchema });
}

/**
 * Substitui todos os campos de uma pessoa da família.
 *
 * A API responde 409 aqui pelo mesmo motivo que na criação: corrigir um nome
 * completo para um que já está cadastrado esbarra na mesma restrição de
 * unicidade, e nesse caso nada é alterado.
 *
 * @param id - Identificador da linha em `pessoas_familia`.
 * @param dados - A pessoa inteira, e não apenas os campos alterados.
 * @returns A pessoa como ficou gravada.
 */
export function atualizarPessoaFamilia(
  id: string,
  dados: NovaPessoaFamilia,
): Promise<PessoaFamilia> {
  return clienteApi.put(`/pessoas-familia/${encodeURIComponent(id)}`, dados, {
    schema: pessoaFamiliaSchema,
  });
}

/**
 * Apaga uma pessoa da família.
 *
 * Não recebe schema porque a API responde 204 sem corpo. A API responde 409
 * quando a pessoa ainda é apontada por documento ou plano de saúde, e nesse caso
 * nada é apagado.
 *
 * @param id - Identificador da linha em `pessoas_familia`.
 */
export function excluirPessoaFamilia(id: string): Promise<void> {
  return clienteApi.remover(`/pessoas-familia/${encodeURIComponent(id)}`);
}
